/**
 * Web Push client — subscribe with VAPID, sync prefs to Pro API.
 */
import { apiFetch, apiConfigured } from './api';
import { getSessionToken } from './entitlement';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export async function getVapidPublicKey() {
  const fromEnv = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';
  if (fromEnv) return fromEnv;
  if (!apiConfigured()) return '';
  try {
    const data = await apiFetch('/push/vapid-public-key');
    return data?.publicKey || '';
  } catch {
    return '';
  }
}

export async function enablePush() {
  if (!pushSupported()) throw new Error('Notifications are not supported in this browser');
  if (!apiConfigured()) throw new Error('API not configured');
  const token = getSessionToken();
  if (!token) throw new Error('Sign in to Pro first');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission denied');

  const vapid = await getVapidPublicKey();
  if (!vapid) throw new Error('VAPID public key missing');

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    });
  }

  const json = sub.toJSON();
  await apiFetch('/push/subscribe', {
    method: 'POST',
    token,
    body: {
      endpoint: json.endpoint,
      keys: json.keys,
    },
  });
  return sub;
}

export async function disablePush() {
  if (!pushSupported()) return;
  const token = getSessionToken();
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    if (token && apiConfigured()) {
      try {
        await apiFetch('/push/subscribe', {
          method: 'DELETE',
          token,
          body: { endpoint },
        });
      } catch { /* ignore */ }
    }
  }
}

export async function fetchPushPrefs() {
  const token = getSessionToken();
  if (!token || !apiConfigured()) return null;
  return apiFetch('/push/prefs', { token });
}

export async function savePushPrefs(prefs) {
  const token = getSessionToken();
  if (!token || !apiConfigured()) throw new Error('Not signed in');
  return apiFetch('/push/prefs', { method: 'PUT', token, body: prefs });
}
